import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Ficha, FichasListResponse, FichaTemplate, Encounter } from './types'

interface FichasFilters {
  organizationId?: string
  operativoId?: string
  template?: FichaTemplate
  from?: string
  to?: string
  limit?: number
  offset?: number
}

export function useFichas(filters: FichasFilters = {}) {
  return useQuery({
    queryKey: ['fichas', filters],
    queryFn: async () => {
      const qs = new URLSearchParams()
      if (filters.organizationId) qs.set('organizationId', filters.organizationId)
      if (filters.operativoId) qs.set('operativoId', filters.operativoId)
      if (filters.template) qs.set('template', filters.template)
      if (filters.from) qs.set('from', filters.from)
      if (filters.to) qs.set('to', filters.to)
      qs.set('limit', String(filters.limit ?? 50))
      qs.set('offset', String(filters.offset ?? 0))
      return api.get<FichasListResponse>(`/api/fichas?${qs.toString()}`)
    },
  })
}

export function useFicha(id: string | undefined) {
  return useQuery({
    queryKey: ['ficha', id],
    queryFn: () => api.get<Ficha>(`/api/fichas/${encodeURIComponent(id as string)}`),
    enabled: !!id,
  })
}

export interface CreateFichaPayload {
  worker_id: string
  encounter_id: string
  template: FichaTemplate
  values: Record<string, unknown>
  custom_fields?: Record<string, unknown>
}

export function useCreateFicha() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateFichaPayload) => api.post<Ficha>('/api/fichas', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['fichas'] })
    },
  })
}

export function useUpdateFicha() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<CreateFichaPayload> }) =>
      api.patch<Ficha>(`/api/fichas/${encodeURIComponent(id)}`, patch),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['fichas'] })
      void qc.invalidateQueries({ queryKey: ['ficha', vars.id] })
    },
  })
}

export function useEncounters(params: { organizationId?: string } = {}) {
  return useQuery({
    queryKey: ['encounters', params],
    queryFn: async () => {
      const qs = new URLSearchParams()
      if (params.organizationId) qs.set('organizationId', params.organizationId)
      return api.get<{ items: Encounter[] }>(`/api/operativos?${qs.toString()}`)
    },
  })
}

export function useWorkersSimple(params: { organizationId?: string } = {}) {
  return useQuery({
    queryKey: ['workers-simple', params],
    queryFn: async () => {
      const qs = new URLSearchParams()
      if (params.organizationId) qs.set('organizationId', params.organizationId)
      qs.set('limit', '500')
      return api.get<{
        items: Array<{ id: string; rut: string; givenName: string; familyName: string }>
      }>(`/api/workers?${qs.toString()}`)
    },
  })
}
